import { useToast } from 'common/hooks/useToast';
import useTranslation from 'common/hooks/useTranslation';
import { selectCurrentCompany } from 'common/slices/auth.slice';
import {
  useGetCompanyDetailQuery,
  useGetCurrentCompanySettingsQuery,
  useSyncCompanyIntegrationsMutation,
  useUpdateCurrentCompanyIntegrationMutation,
} from 'common/slices/companyApi.slice';
import React, { createContext, useContext, useState } from 'react';
import { useSelector } from 'react-redux';

export const IntegrationSettingsContext = createContext(null);

export const useIntegrationSettings = () => {
  const ctx = useContext(IntegrationSettingsContext);
  if (!ctx) {
    throw new Error('useIntegrationSettings must be used within a Provider');
  }
  return ctx;
};

export const IntegrationSettingsProvider = ({ children }) => {
  const toast = useToast();
  const { t, i18n } = useTranslation('settings\\index');
  const currentCompany = useSelector(selectCurrentCompany);

  const {
    data: companySettings,
    isLoading: isLoadingCompanySettings,
    refetch: refetchCompanySettings,
  } = useGetCurrentCompanySettingsQuery({ companyId: currentCompany.id });

  const [
    updateCurrentCompanyIntegration,
    { isLoading: isUpdatingCompanySettings },
  ] = useUpdateCurrentCompanyIntegrationMutation();

  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [currentTab, setCurrentTab] = useState(null);
  const [isSAPSFFormValid, setIsSAPSFFormValid] = useState(false);

  const handleCardClick = async (integration) => {
    setSelectedIntegration(integration);

    if (companyDetail?.active_integration_status === 'IS_SYNCING') {
      try {
        await Promise.all([refetchCompanyDetail(), refetchCompanySettings()]);
      } catch (error) {
        console.error('Failed to refetch company detail:', error);
        toast.newToast('negative', t('toasts.something-wrong'));
      }
    }
  };

  const handleSave = async (values) => {
    const settings = Object.entries(values).map(([key, value]) => ({
      name: key,
      value: typeof value === 'boolean' ? String(value) : value,
    }));

    try {
      await updateCurrentCompanyIntegration({
        companySlug: currentCompany.slug,
        integrationSlug: selectedIntegration.slug,
        data: settings,
      }).unwrap();

      toast.newToast('positive', t('toasts.success'));
      setSelectedIntegration(null);
    } catch (err) {
      if (err?.status === 403) {
        toast.newToast('negative', t('toasts.forbidden'));
      } else {
        toast.newToast('negative', t('toasts.something-wrong'));
      }
    }
  };

  const [syncCompanyIntegrations, { isLoading: isSyncingIntegration }] =
    useSyncCompanyIntegrationsMutation();

  const handleSynchronize = async () => {
    try {
      await syncCompanyIntegrations({
        companySlug: currentCompany.slug,
        integrationName: selectedIntegration.slug,
      }).unwrap();
      //setSelectedIntegration(null);

      toast.newToast('positive', t('toasts.successful-sync-try'));
    } catch (err) {
      if (err?.status === 403) {
        toast.newToast('negative', t('toasts.forbidden'));
      } else {
        toast.newToast('negative', t('toasts.something-wrong'));
      }
    }
  };

  const { data: companyDetail, refetch: refetchCompanyDetail } =
    useGetCompanyDetailQuery({
      slug: currentCompany.slug,
    });

  return (
    <IntegrationSettingsContext.Provider
      value={{
        currentTab,
        setCurrentTab,
        selectedIntegration,
        setSelectedIntegration,
        companySettings,
        isLoadingCompanySettings,
        isUpdatingCompanySettings,
        handleSave,
        handleSynchronize,
        isSyncingIntegration,
        companyDetail,
        refetchCompanyDetail,
        isSAPSFFormValid,
        setIsSAPSFFormValid,
        handleCardClick,
        t,
        i18n,
      }}
    >
      {children}
    </IntegrationSettingsContext.Provider>
  );
};
